import { FC, ReactElement, ReactNode, SVGProps } from 'react';
import { motion } from 'framer-motion';

import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/utils/cn';

import { Button } from './button';
import { Icons } from './icons';

interface DrawerProps {
  isOpen: boolean;
  title: string;
  currentStep: number;
  totalSteps: number;
  children: ReactNode;
  backButtonText: string;
  nextButtonText: string;
  nextButton?: ReactNode;
  isNextDisabled: boolean;
  showBackButton?: boolean;
  showNextButton?: boolean;
  nextButtonIcon?: ReactElement<SVGProps<SVGSVGElement>>;
  trigger?: ReactNode;
  onOpenChange: (open: boolean) => void;
  onCancel?: () => void;
  onBack: () => void;
  onNext: () => void;
}

const Drawer: FC<DrawerProps> = ({
  isOpen,
  title,
  currentStep,
  totalSteps,
  children,
  backButtonText,
  nextButtonText,
  nextButton,
  isNextDisabled,
  showBackButton = true,
  nextButtonIcon,
  showNextButton = true,
  trigger = null,
  onOpenChange,
  onCancel,
  onBack,
  onNext,
}) => {
  const progress = (currentStep / totalSteps) * 100;

  const handleOnNext = () => {
    onNext();
  };

  const handleOnBack = () => {
    onBack();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      {trigger}
      <Dialog.Portal>
        <Dialog.Overlay className='fixed inset-0 bg-black/50 animate-fade-in z-50' />
        <Dialog.Content
          className={cn(
            'fixed bottom-0 right-0 flex flex-col h-full lg:w-[530px] w-full bg-surface-primary  lg:border-l-2 border-border-primary animate-fade-in z-50 outline-none',
          )}
        >
          <Dialog.Description className='hidden'>Ducat Vault Action</Dialog.Description>
          <div className='flex items-center justify-between lg:px-8 px-4 py-6 relative'>
            <div />
            <Dialog.Title className='text-text-primary tracking-02em leading-7 lg:text-2xl text-xl'>{title}</Dialog.Title>
            <Button variant='tertiary' onClick={() => (onCancel ? onCancel() : onOpenChange(false))}>
              <Icons.close />
            </Button>
            {totalSteps === 1 ? (
              <div className='absolute bottom-0 w-full h-px bg-border-primary left-0' />
            ) : (
              <div className='absolute bottom-0 w-full h-2 bg-border-primary left-0'>
                <motion.div
                  className='h-full bg-text-primary rounded-l-lg rounded-r-lg'
                  style={{ width: `${progress}%` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            )}
          </div>
          <div className='relative flex flex-1 min-h-0 px-4 lg:pl-20 lg:pr-[72px] py-8 scrollbar-gutter-stable overflow-x-auto overflow-y-auto'>
            {children}
          </div>
          <div className='flex flex-col lg:flex-row items-center justify-center gap-3 lg:gap-6 w-full py-5 px-6 lg:px-20 lg:border-t-2 border-border-primary shrink-0 bg-surface-primary'>
            {showBackButton && (
              <Button onClick={handleOnBack} variant='secondary' className='!w-full lg:w-auto'>
                {backButtonText}
              </Button>
            )}
            {nextButton}
            {!nextButton && showNextButton && (
              <Button onClick={handleOnNext} disabled={isNextDisabled} trailingIcon={nextButtonIcon} className='!w-full lg:w-auto'>
                {nextButtonText}
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Drawer;
